import * as RadixSelect from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { useFieldContext } from './Field.js';

export interface SelectOption {
  value: string;
  label: string;
  /** Mono secondary text right of the label. */
  hint?: string;
  disabled?: boolean;
}

export interface SelectGroup {
  label: string;
  options: SelectOption[];
}

export interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  options: SelectOption[] | SelectGroup[];
  placeholder?: string;
  disabled?: boolean;
  /** Accessible name when not inside a Field. */
  'aria-label'?: string;
  id?: string;
  name?: string;
  mono?: boolean;
  className?: string;
}

function isGrouped(options: SelectOption[] | SelectGroup[]): options is SelectGroup[] {
  return options.length > 0 && 'options' in (options[0] as SelectGroup);
}

/** Radix Select styled as an inset control; the list is a hairline panel with no shadow. */
export function Select({ value, defaultValue, onValueChange, options, placeholder = 'Select', disabled, id, name, mono = false, className, ...aria }: SelectProps) {
  const field = useFieldContext();
  const renderItem = (o: SelectOption) => (
    <RadixSelect.Item
      key={o.value}
      value={o.value}
      disabled={o.disabled}
      className={cn(
        'relative flex cursor-default select-none items-center justify-between gap-4 rounded-sm px-2 py-1.5 pl-6 text-nav text-ink outline-none data-[disabled]:opacity-50 data-[highlighted]:bg-card',
        mono ? 'font-mono text-xs' : '',
      )}
    >
      <RadixSelect.ItemIndicator className="absolute left-1.5 top-1/2 -translate-y-1/2 text-brand-text">
        <Check size={11} aria-hidden />
      </RadixSelect.ItemIndicator>
      <RadixSelect.ItemText>{o.label}</RadixSelect.ItemText>
      {o.hint ? <span className="font-mono text-2xs text-mute">{o.hint}</span> : null}
    </RadixSelect.Item>
  );
  return (
    <RadixSelect.Root value={value} defaultValue={defaultValue} onValueChange={onValueChange} disabled={disabled} name={name}>
      <RadixSelect.Trigger
        id={id ?? field?.id}
        aria-label={aria['aria-label']}
        aria-describedby={field?.describedBy}
        aria-invalid={field?.invalid ? true : undefined}
        className={cn(
          'inline-flex w-full items-center justify-between gap-2 rounded-sm border border-line bg-bg px-2.5 py-1.5 text-nav text-ink transition-colors hover:border-mute/60 focus:border-brand focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-mute aria-invalid:border-brand',
          mono ? 'font-mono text-xs' : '',
          className,
        )}
      >
        <span className="truncate">
          <RadixSelect.Value placeholder={placeholder} />
        </span>
        <RadixSelect.Icon>
          <ChevronDown size={12} aria-hidden className="text-mute" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={4}
          className="z-[95] max-h-[min(320px,var(--radix-select-content-available-height))] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-sm border border-line bg-panel"
        >
          <RadixSelect.Viewport className="scrollbar-thin p-1">
            {isGrouped(options)
              ? options.map((g) => (
                  <RadixSelect.Group key={g.label}>
                    <RadixSelect.Label className="label-mono px-2 pb-1 pt-2 text-mute">{g.label}</RadixSelect.Label>
                    {g.options.map(renderItem)}
                  </RadixSelect.Group>
                ))
              : options.map(renderItem)}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
